import React, { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { useData } from '../../contexts/DataContext';
import { toast } from 'sonner';
import Modal from '../common/Modal';
import Button from '../common/Button';
import DriveAttachGate from '../files/DriveAttachGate';
import { uploadTaskFilesToDrive, formatUploadErrors } from '../../utils/driveUpload';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TaskCompletionModal = ({ isOpen, onClose, task, onComplete }) => {
  const { completeTask } = useData();
  const [loading, setLoading] = useState(false);
  const [completionNote, setCompletionNote] = useState('');
  const [completionFiles, setCompletionFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0, filename: '' });
  const [isRecording, setIsRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);

  // All hooks must be called before any conditional returns
  const handleFileChange = useCallback((e) => {
    const files = Array.from(e.target.files);
    setCompletionFiles(prev => [...prev, ...files]);
  }, []);

  const removeFile = useCallback((index) => {
    setCompletionFiles(prev => prev.filter((_, i) => i !== index));
  }, []);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setCompletionNote('');
      setCompletionFiles([]);
      setUploadProgress({ current: 0, total: 0, filename: '' });
      setIsRecording(false);
    }
  }, [isOpen]);

  // Early return AFTER all hooks
  if (!isOpen || !task) return null;

  // Audio recording for completion
  const startRecording = async () => {
    try {
      // Check if navigator.mediaDevices is available
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        alert("Audio recording is not supported in this browser or requires HTTPS.");
        return;
      }

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      const chunks = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunks.push(e.data);
        }
      };

      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: 'audio/webm' });
        const file = new File([blob], `completion-audio-${Date.now()}.webm`, { type: 'audio/webm' });
        setCompletionFiles(prev => [...prev, file]);
        stream.getTracks().forEach(track => track.stop());
      };

      setMediaRecorder(recorder);
      recorder.start();
      setIsRecording(true);
    } catch (error) {
      console.error("Error accessing microphone:", error);
      if (error.name === 'NotAllowedError') {
        alert("Microphone access denied. Please allow microphone access in your browser settings and try again.");
      } else if (error.name === 'NotFoundError') {
        alert("No microphone found. Please connect a microphone and try again.");
      } else if (error.name === 'NotSupportedError') {
        alert("Audio recording is not supported in this browser.");
      } else {
        alert("Unable to access microphone. Please check your browser settings and permissions.");
      }
    }
  };

  const stopRecording = () => {
    if (mediaRecorder && mediaRecorder.state === 'recording') {
      mediaRecorder.stop();
      setIsRecording(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!task || !task.id) {
      alert('Task information is missing');
      return;
    }

    setLoading(true);

    try {
      // Complete the task with completion details using DataContext
      const completionData = {
        completion_notes: completionNote,
        completed_at: new Date().toISOString()
      };

      console.log('Completing task:', task.id, completionData);
      const completionResponse = await completeTask(task.id, completionData);

      // Upload completion files if any
      if (completionFiles.length > 0) {
        setUploadProgress({ current: 0, total: completionFiles.length, filename: '' });
        
        const uploadErrors = await uploadTaskFilesToDrive(task.id, completionFiles, {
          fileType: 'completion',
          onFileStart: (i, file) => setUploadProgress({ current: i + 1, total: completionFiles.length, filename: file.name })
        });
        if (uploadErrors.length) toast.error(`Some files were not attached:\n${formatUploadErrors(uploadErrors)}`);
      }

      // Call completion callback if provided
      if (onComplete && typeof onComplete === 'function') {
        onComplete();
      }
      
      onClose();
      
      toast.success('Task completed');
    } catch (error) {
      console.error("Error completing task:", error);
      if (error.response?.status === 403) {
        toast.error(error.response.data.detail || "Permission denied. Please check your subscription status.");
      } else {
        toast.error("Failed to complete task. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const getFileIcon = (file) => {
    if (file.type?.startsWith('image/')) {
      return '🖼️';
    } else if (file.type?.startsWith('audio/')) {
      return '🎵';
    } else if (file.type?.includes('pdf')) {
      return '📄';
    } else if (file.type?.includes('document') || file.type?.includes('word')) {
      return '📝';
    }
    return '📎';
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Complete Task"
      size="lg"
    >
      <div className="p-6">
        <div className="mb-4">
          <h3 className="font-medium text-gray-900 mb-2">{task.title}</h3>
          <p className="text-sm text-gray-600">Mark this task as completed and add any completion details.</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Completion Note */}
          <div>
            <label className="block text-gray-700 text-sm font-medium mb-2">
              Completion Notes
            </label>
            <textarea
              value={completionNote}
              onChange={(e) => setCompletionNote(e.target.value)}
              rows={4}
              className="mobile-input resize-none"
              placeholder="Add notes about how you completed this task..."
            />
          </div>

          {/* Completion Files & Media */}
          <div>
            <label className="block text-gray-700 text-sm font-medium mb-2">
              Completion Files & Media
            </label>
            
            <DriveAttachGate>
            <div className="grid grid-cols-3 gap-4">
              {/* File Upload */}
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-green-400 transition-colors">
                <input
                  type="file"
                  multiple
                  onChange={handleFileChange}
                  className="hidden"
                  id="completion-file-upload"
                  accept="*/*"
                />
                <label htmlFor="completion-file-upload" className="cursor-pointer">
                  <div className="text-2xl mb-2">📁</div>
                  <p className="text-sm text-gray-600">Upload Files</p>
                </label>
              </div>

              {/* Audio Recording */}
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                <button
                  type="button"
                  onClick={isRecording ? stopRecording : startRecording}
                  className={`text-2xl mb-2 ${isRecording ? 'animate-pulse text-red-500' : 'text-gray-400'}`}
                >
                  {isRecording ? '⏹️' : '🎤'}
                </button>
                <p className="text-sm text-gray-600">
                  {isRecording ? 'Recording...' : 'Record Audio'}
                </p>
              </div>

              {/* Photo */}
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={handleFileChange}
                  className="hidden"
                  id="completion-photo"
                />
                <label htmlFor="completion-photo" className="cursor-pointer">
                  <div className="text-2xl mb-2">📷</div>
                  <p className="text-sm text-gray-600">Take Photo</p>
                </label>
              </div>
            </div>
            </DriveAttachGate>

            {/* Files Preview */}
            {completionFiles.length > 0 && (
              <div className="mt-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">
                  Completion Files ({completionFiles.length})
                </h4>
                <div className="space-y-2 max-h-32 overflow-y-auto">
                  {completionFiles.map((file, index) => (
                    <div key={index} className="flex items-center justify-between bg-green-50 rounded-lg p-3">
                      <div className="flex items-center space-x-3">
                        <span className="text-lg">{getFileIcon(file)}</span>
                        <div>
                          <p className="text-sm font-medium text-gray-900 truncate max-w-48">
                            {file.name}
                          </p>
                          <p className="text-xs text-gray-500">
                            {(file.size / 1024 / 1024).toFixed(2)} MB
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeFile(index)}
                        className="text-red-500 hover:text-red-700 p-1"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Upload Progress */}
            {uploadProgress.total > 0 && (
              <div className="mt-4 p-3 bg-green-50 rounded-lg">
                <div className="flex items-center justify-between text-sm">
                  <span>Uploading: {uploadProgress.filename}</span>
                  <span>{uploadProgress.current}/{uploadProgress.total}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                  <div 
                    className="bg-green-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
                  ></div>
                </div>
              </div>
            )}
          </div>

          {/* Form Actions */}
          <div className="flex space-x-3 pt-4 border-t">
            <Button
              type="button"
              variant="secondary"
              fullWidth
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              fullWidth
              loading={loading}
              disabled={loading}
            >
              {loading ? 'Completing...' : 'Complete Task'}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default TaskCompletionModal;